import { useAuth } from "../../context/auth-context";
import type {
  AdminBusinessTransactionsData,
  AdminBusinessTransactionsEnvelope,
  BusinessTransactionStatusFilter,
  BusinessTransactionType,
} from "./adminBusinessTransactions";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";

const DEFAULT_LIMIT = 25;

export type UseAdminBusinessTransactionsParams = {
  page: number;
  type: BusinessTransactionType;
  /** "all" omits the param entirely. */
  status?: BusinessTransactionStatusFilter;
  from?: string;
  to?: string;
  businessId?: string;
  limit?: number;
  enabled?: boolean;
};

/**
 * Paginated rows from GET api/businesses/admin/transactions
 * (?type=deposits|payouts[&status][&from][&to][&businessId]).
 */
export function useAdminBusinessTransactionsQuery(
  params: UseAdminBusinessTransactionsParams
) {
  const { isAuthenticated } = useAuth();
  const {
    page,
    type,
    status = "all",
    from,
    to,
    businessId,
    limit = DEFAULT_LIMIT,
    enabled: enabledParam = true,
  } = params;

  const statusParam = status === "all" ? undefined : status;

  return useApiQuery<
    AdminBusinessTransactionsEnvelope,
    AdminBusinessTransactionsData
  >({
    queryKey: apiQueryKey("business-transactions", {
      type,
      page,
      limit,
      status: statusParam ?? null,
      from: from ?? null,
      to: to ?? null,
      businessId: businessId ?? null,
    }),
    url: "api/businesses/admin/transactions",
    params: {
      type,
      page,
      limit,
      status: statusParam,
      from,
      to,
      businessId,
    },
    enabled: Boolean(isAuthenticated) && enabledParam,
    select: (body) => body.data,
  });
}
